import { useState, useEffect, FormEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PageHeader from '../../components/PageHeader';
import LoadingSpinner from '../../components/LoadingSpinner';
import { vendorApi } from '../../services/vendor';
import extractApiError from '../../utils/extractApiError';

interface GuardFormData {
  first_name: string;
  last_name: string;
  external_id: string;
  email: string;
  phone: string;
}

export default function VendorGuardForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = !!id;

  const [form, setForm] = useState<GuardFormData>({
    first_name: '',
    last_name: '',
    external_id: '',
    email: '',
    phone: '',
  });
  const [showAssignPrompt, setShowAssignPrompt] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load existing guard if editing
  const { data: guard, isLoading: guardLoading } = useQuery({
    queryKey: ['vendor', 'guards', id],
    queryFn: () => vendorApi.getGuard(id!).then((r) => r.data),
    enabled: isEdit,
  });

  // Populate form when guard data loads
  useEffect(() => {
    if (guard) {
      setForm({
        first_name: guard.first_name,
        last_name: guard.last_name,
        external_id: guard.external_id || '',
        email: guard.email || '',
        phone: guard.phone || '',
      });
      // guard loaded for editing
    }
  }, [guard]);

  const createMutation = useMutation({
    mutationFn: (data: { first_name: string; last_name: string; external_id?: string; email?: string; phone?: string }) =>
      vendorApi.createGuard(data),
  });

  const updateMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => vendorApi.updateGuard(id!, data),
  });

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    const payload: Record<string, unknown> = { ...form };
    // Remove empty optional fields
    if (!payload.external_id) delete payload.external_id;
    if (!payload.email) delete payload.email;
    if (!payload.phone) delete payload.phone;

    try {
      let guardId: string;

      if (isEdit) {
        await updateMutation.mutateAsync(payload);
        guardId = id!;
      } else {
        const response = await createMutation.mutateAsync(
          payload as { first_name: string; last_name: string },
        );
        guardId = response.data.id;
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['vendor', 'guards'] }),
        queryClient.invalidateQueries({ queryKey: ['vendor', 'sites'] }),
      ]);
      if (!isEdit) {
        setShowAssignPrompt(true);
        setTimeout(() => navigate('/vendor/guard-assignment'), 3000);
      } else {
        navigate(`/vendor/guards/${guardId}`);
      }
    } catch (err: unknown) {
      setError(extractApiError(err));
    }
  };

  if (isEdit && guardLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <>
      <PageHeader
        title={isEdit ? 'Edit Guard' : 'Add Guard'}
        description={isEdit ? `Editing ${guard?.name}` : 'Register a new security guard'}
      />

      <form onSubmit={handleSubmit} className="max-w-2xl">
        {error && (
          <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="rounded-2xl border border-slate-100 bg-white p-6 mb-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Guard Details</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="first_name" className="block text-sm font-medium text-slate-700 mb-1">
                First Name *
              </label>
              <input
                id="first_name"
                type="text"
                required
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                className="w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>
            <div>
              <label htmlFor="last_name" className="block text-sm font-medium text-slate-700 mb-1">
                Last Name *
              </label>
              <input
                id="last_name"
                type="text"
                required
                value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                className="w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-1">
                Phone
              </label>
              <input
                id="phone"
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="external_id" className="block text-sm font-medium text-slate-700 mb-1">
                External ID
              </label>
              <input
                id="external_id"
                type="text"
                value={form.external_id}
                onChange={(e) => setForm({ ...form, external_id: e.target.value })}
                className="w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                placeholder="Optional internal tracking ID"
              />
            </div>
          </div>
        </div>

        {showAssignPrompt && (
          <div className="mb-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-center">
            <p className="text-sm font-medium text-emerald-800">Guard created successfully!</p>
            <p className="text-xs text-emerald-600 mt-1">Redirecting to Guard Assignment to assign to a site...</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <button
            type="submit"
            disabled={isSaving}
            className="px-6 py-2 text-sm font-medium text-white bg-brand-600 rounded-xl hover:bg-brand-700 disabled:opacity-50"
          >
            {isSaving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Guard'}
          </button>
          <button
            type="button"
            onClick={() => navigate(isEdit ? `/vendor/guards/${id}` : '/vendor/guards')}
            className="px-6 py-2 text-sm font-medium text-slate-700 bg-white border-2 border-slate-200 rounded-xl hover:bg-slate-50"
          >
            Cancel
          </button>
        </div>
      </form>
    </>
  );
}
